import React, { useState, useCallback, useRef, useEffect } from 'react';
import {
    TextField,
    FormControl,
    CircularProgress,
    Box,
    Paper,
    List,
    ListItem,
    ListItemButton,
    ListItemText,
    Typography,
} from '@mui/material';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import { FormField } from './utils/formFieldTypes';
import { renderFieldLabel, renderError } from './utils/formDataHandler';
import { debounce } from 'lodash';

interface GooglePlacesAutocompleteProps {
    field: FormField;
    value: string;
    onChange: (field: FormField) => void;
    onAddressSelected: (placeResult: google.maps.places.PlaceResult, systemIdentifier: string) => void;
    errorMessage?: string;
    countryCode?: string; // Optional country code to limit search region (e.g., 'US', 'AU', 'GB')
}

interface Suggestion {
    placeId: string;
    description: string;
    mainText: string;
    secondaryText: string;
}

const GooglePlacesAutocomplete: React.FC<GooglePlacesAutocompleteProps> = ({
    field,
    value,
    onChange,
    onAddressSelected,
    errorMessage = '',
    countryCode,
}) => {
    const [input, setInput] = useState(value || '');
    const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
    const [loading, setLoading] = useState(false);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [error, setError] = useState('');
    const autocompleteServiceRef = useRef<google.maps.places.AutocompleteService | null>(null);
    const placesServiceRef = useRef<google.maps.places.PlacesService | null>(null);
    const sessionTokenRef = useRef<google.maps.places.AutocompleteSessionToken | null>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const listRef = useRef<HTMLUListElement>(null);

    /**
     * Initialize Google Places services
     */
    useEffect(() => {
        const initializeGooglePlaces = () => {
            if (!window.google?.maps?.places) {
                console.error('Google Maps Places API not loaded');
                setError('Address service is unavailable. Please try again later.');
                return;
            }

            autocompleteServiceRef.current = new google.maps.places.AutocompleteService();
            sessionTokenRef.current = new google.maps.places.AutocompleteSessionToken();

            // Initialize PlacesService with a dummy map element
            const dummyDiv = document.createElement('div');
            const dummyMap = new google.maps.Map(dummyDiv);
            placesServiceRef.current = new google.maps.places.PlacesService(dummyMap);
        };

        initializeGooglePlaces();
    }, []);

    /**
     * Fetch autocomplete predictions with debouncing
     */
    const fetchPredictions = useCallback(
        debounce(async (inputValue: string) => {
            if (!inputValue?.trim() || inputValue.length < 3) {
                setSuggestions([]);
                setShowSuggestions(false);
                return;
            }

            if (!autocompleteServiceRef.current) {
                setError('Address service is unavailable');
                return;
            }

            setLoading(true);
            setError('');

            try {
                const request: google.maps.places.AutocompleteRequest = {
                    input: inputValue,
                    sessionToken: sessionTokenRef?.current ?? undefined,
                    componentRestrictions: {
                        country: countryCode?.toLowerCase() || 'au',
                    },
                    // Use provided country code, fallback to 'au'
                    region: countryCode?.toLowerCase() || 'au',
                };

                const response = await autocompleteServiceRef.current.getPlacePredictions(request);

                if (response.predictions && response.predictions.length > 0) {
                    const formattedSuggestions: Suggestion[] = response.predictions.map((prediction) => ({
                        placeId: prediction.place_id,
                        description: prediction.description,
                        mainText: prediction.structured_formatting?.main_text || prediction.description,
                        secondaryText: prediction.structured_formatting?.secondary_text || '',
                    }));

                    setSuggestions(formattedSuggestions);
                    setShowSuggestions(true);
                } else {
                    setSuggestions([]);
                }
            } catch (err) {
                console.error('Error fetching predictions:', err);
                setError('Failed to fetch address suggestions');
                setSuggestions([]);
            } finally {
                setLoading(false);
            }
        }, 300),
        [countryCode]
    );

    /**
     * Handle input change
     */
    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = e.target.value;
        setInput(newValue);

        // Ensure the field object is properly passed with the updated value
        const updatedField: FormField = {
            ...field,
            value: newValue,
        };
        onChange(updatedField);
        fetchPredictions(newValue);
    };

    /**
     * Get detailed place information and extract address components
     */
    const handleSelectAddress = useCallback(
        async (suggestion: Suggestion) => {
            if (!placesServiceRef.current) {
                setError('Address service is unavailable');
                return;
            }

            setLoading(true);

            try {
                const request: google.maps.places.PlaceDetailsRequest = {
                    placeId: suggestion.placeId,
                    sessionToken: sessionTokenRef.current ?? undefined,
                    fields: [
                        'formatted_address',
                        'address_components',
                        'geometry',
                        'place_id',
                        'url',
                        'name',
                    ],
                };

                placesServiceRef.current.getDetails(request, (placeResult, status) => {
                    if (status !== google.maps.places.PlacesServiceStatus.OK || !placeResult) {
                        console.error('Error getting place details:', status);
                        setError('Failed to load address details');
                        setLoading(false);
                        return;
                    }

                    const formattedAddress = placeResult.formatted_address || suggestion.description;

                    setInput(formattedAddress);

                    setSuggestions([]);
                    setShowSuggestions(false);

                    sessionTokenRef.current = new google.maps.places.AutocompleteSessionToken();

                    onAddressSelected(placeResult, field.system_identifire);

                    setLoading(false);
                });
            } catch (err) {
                console.error('Error selecting address:', err);
                setError('Failed to select address');
                setLoading(false);
            }
        },
        [field, onChange, onAddressSelected]
    );

    /**
     * Handle clicking outside to close suggestions
     */
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
                setShowSuggestions(false);
            }
        };

        if (showSuggestions) {
            document.addEventListener('mousedown', handleClickOutside);
            return () => document.removeEventListener('mousedown', handleClickOutside);
        }
    }, [showSuggestions]);

    /**
     * Handle keyboard navigation
     */
    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Escape') {
            setShowSuggestions(false);
        }
    };

    return (
        <FormControl fullWidth margin="normal" ref={containerRef} sx={{ position: 'relative' }}>
            {renderFieldLabel(field)}
            <Box sx={{ position: 'relative' }}>
                <TextField
                    name={field.id.toString()}
                    value={input}
                    variant="outlined"
                    onChange={handleInputChange}
                    onKeyDown={handleKeyDown}
                    disabled={field.disabled}
                    placeholder="Search for an address..."
                    autoComplete="off"
                    InputProps={{
                        endAdornment: loading ? (
                            <CircularProgress color="primary" size={20} sx={{ mr: 1 }} />
                        ) : null,
                    }}
                    sx={{
                        width: '100%',
                        '& .MuiOutlinedInput-root': {
                            borderRadius: '50px',
                            backgroundColor: 'transparent',
                            color: '#fff',
                            '& fieldset': {
                                borderColor: errorMessage ? '#f44336' : '#a3a3a3',
                                borderWidth: '1px',
                            },
                            '&:hover fieldset': {
                                borderColor: errorMessage ? '#f44336' : '#d0d0d0',
                            },
                            '&.Mui-focused fieldset': {
                                borderColor: errorMessage ? '#f44336' : '#f2674a',
                                borderWidth: '1px',
                            },
                            '& input': {
                                color: '#fff',
                                paddingLeft: '20px',
                                '&::placeholder': {
                                    color: '#a3a3a3',
                                    opacity: 1,
                                },
                                '&.Mui-disabled': {
                                    color: '#fff',
                                    WebkitTextFillColor: '#fff',
                                },
                            },
                        },
                    }}
                />

                {/* Suggestions Dropdown */}
                {showSuggestions && suggestions.length > 0 && (
                    <Paper
                        elevation={3}
                        sx={{
                            position: 'absolute',
                            top: 'calc(100% + 8px)',
                            left: 0,
                            right: 0,
                            zIndex: 1300,
                            maxHeight: '300px',
                            overflowY: 'auto',

                            borderRadius: '14px',
                            backgroundColor: 'rgba(30,30,30,0.95)',
                            backdropFilter: 'blur(8px)',

                            border: '1px solid rgba(255,255,255,0.08)',
                            boxShadow: '0px 10px 30px rgba(0,0,0,0.55)',
                            pr: 1.5,

                            /* ---------- Modern Scrollbar ---------- */

                            // Firefox
                            scrollbarWidth: 'thin',
                            scrollbarColor: 'rgba(255,255,255,0.25) transparent',

                            // Chrome / Edge / Safari
                            '&::-webkit-scrollbar': {
                                width: '6px',
                            },
                            '&::-webkit-scrollbar-track': {
                                background: 'transparent',
                                margin: '8px 0',
                            },
                            '&::-webkit-scrollbar-thumb': {
                                backgroundColor: 'rgba(255,255,255,0.18)',
                                borderRadius: '10px',
                                transition: 'all 0.2s ease',
                            },
                            '&::-webkit-scrollbar-thumb:hover': {
                                backgroundColor: 'rgba(255,255,255,0.35)',
                            },

                            animation: 'fadeIn 0.15s ease-out',

                            '@keyframes fadeIn': {
                                from: { opacity: 0, transform: 'translateY(-4px)' },
                                to: { opacity: 1, transform: 'translateY(0)' },
                            },
                        }}
                    >
                        <List ref={listRef} sx={{ py: 0 }}>
                            {suggestions.map((suggestion, index) => (
                                <ListItem key={suggestion.placeId} disablePadding>
                                    <ListItemButton
                                        onClick={() => handleSelectAddress(suggestion)}
                                        sx={{
                                            py: 1.5,
                                            px: 2.5,
                                            color: '#fff !important',
                                            transition: 'background-color 0.15s ease',
                                            borderBottom: index < suggestions.length - 1 ? '1px solid #2e2e2e' : 'none',
                                            '&:hover': {
                                                backgroundColor: '#2a2a2a',
                                                color: '#fff !important',
                                            },
                                            '&:active': {
                                                backgroundColor: '#333',
                                                color: '#fff !important',
                                            },
                                        }}
                                    >
                                        <LocationOnIcon
                                            sx={{
                                                mr: 1.5,
                                                color: '#f2674a',
                                                flexShrink: 0,
                                                fontSize: '20px',
                                            }}
                                        />
                                        <ListItemText
                                            primary={suggestion.mainText}
                                            secondary={suggestion.secondaryText}
                                            primaryTypographyProps={{
                                                sx: {
                                                    fontSize: '0.9rem',
                                                    fontWeight: 500,
                                                    color: '#ffffff !important',         // ← bright white for main address text
                                                    lineHeight: 1.3,
                                                },
                                            }}
                                            secondaryTypographyProps={{
                                                sx: {
                                                    fontSize: '0.78rem',
                                                    color: '#a3a3a3 !important',
                                                    mt: 0.25,
                                                    lineHeight: 1.4,
                                                },
                                            }}
                                        />
                                    </ListItemButton>
                                </ListItem>
                            ))}
                        </List>
                    </Paper>
                )}

                {/* No Results Message */}
                {showSuggestions && input.length >= 3 && suggestions.length === 0 && !loading && (
                    <Paper
                        elevation={3}
                        sx={{
                            position: 'absolute',
                            top: 'calc(100% + 6px)',
                            left: 0,
                            right: 0,
                            zIndex: 1300,
                            backgroundColor: '#1e1e1e',
                            border: '1px solid #a3a3a3',
                            borderRadius: '12px',
                            p: 2,
                            textAlign: 'center',
                        }}
                    >
                        <Typography variant="body2" sx={{ color: '#a3a3a3', fontSize: '0.85rem' }}>
                            No addresses found. Try refining your search.
                        </Typography>
                    </Paper>
                )}
            </Box>

            {/* Error Messages */}
            {renderError(errorMessage)}
            {error && renderError(error)}
        </FormControl>
    );
};

export default GooglePlacesAutocomplete;
